import dayjs from 'dayjs'
import { useAtomValue } from 'jotai'
import React, { createContext, useCallback, useContext, useState } from 'react'

import { isAuthenticatedAtom } from '../domains/auth/AtomAuth'
import { authDataAtom } from '../domains/auth/AtomAuth'
import PrivateSessionService from '../services/PrivateSessionService'
import { isInstructor } from '../share/utility'

const SessionContext = createContext()

export const SessionProvider = ({ children }) => {
  const isAuthenticated = useAtomValue(isAuthenticatedAtom)
  const authData = useAtomValue(authDataAtom)

  const [availableSessions, setAvailableSessions] = useState([])
  const [myPrivateSessions, setMyPrivateSessions] = useState([])
  const [myInstructorSessions, setMyInstructorSessions] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  // Fetch all available sessions from today
  const fetchAvailableSessions = useCallback(async () => {
    if (availableSessions.length === 0) {
      setIsLoading(true)
    }
    setError(null)
    try {
      const filters = {
        status: 'AVAILABLE',
        size: -1,
        dateAfter: dayjs().valueOf(),
      }
      const data = await PrivateSessionService.getPrivateSessions(filters)
      setAvailableSessions(data)
      return data
    } catch (err) {
      setError(err.message)
      console.error('Error fetching available sessions:', err)
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Fetch current user's private sessions
  const fetchMyPrivateSessions = useCallback(async () => {
    if (!isAuthenticated) {
      setMyPrivateSessions([])
      return []
    }

    if (myInstructorSessions.length === 0) {
      setIsLoading(true)
    }

    setError(null)
    try {
      const filters = {
        userId: authData.user.resourceId,
        size: -1,
      }
      const data = await PrivateSessionService.getPrivateSessions(filters)
      setMyPrivateSessions(data)
      setIsLoading(false)
      return data
    } catch (err) {
      setError(err.message)
      console.error('Error fetching my private sessions:', err)
    } finally {
      setIsLoading(false)
    }
  }, [isAuthenticated, authData])

  // Fetch current instructor's sessions
  const fetchMyInstructorSessions = useCallback(async () => {
    if (!isAuthenticated || !isInstructor(authData?.user)) {
      setMyInstructorSessions([])
      return []
    }

    setIsLoading(true)
    setError(null)
    try {
      const filters = {
        instructorId: authData.user.resourceId,
        size: -1,
      }
      const data = await PrivateSessionService.getPrivateSessions(filters)
      setMyInstructorSessions(data)
      return data
    } catch (err) {
      setError(err.message)
      console.error('Error fetching my instructor sessions:', err)
    } finally {
      setIsLoading(false)
    }
  }, [isAuthenticated, authData])

  const reset = () => {
    setAvailableSessions([])
    setMyPrivateSessions([])
    setMyInstructorSessions([])
    setIsLoading(false)
    setError(null)
  }

  const value = {
    // State
    availableSessions,
    myPrivateSessions,
    myInstructorSessions,
    isLoading,
    error,

    // Methods
    fetchAvailableSessions,
    fetchMyPrivateSessions,
    fetchMyInstructorSessions,
    reset,
  }

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

// Custom hook to use the session context
export const usePrivateSession = () => {
  const context = useContext(SessionContext)
  if (!context) {
    throw new Error('usePrivateSession must be used within a SessionProvider')
  }
  return context
}
